import { truncateCourt } from "../../utils/truncateCourt";
import {
  formatYmd,
  getSelectedDiadikasia,
  isDikasimosRescheduled,
  parseDikasimos,
} from "../../features/cases/caseFilters";

// Timestamps arrive as MySQL UTC strings ("YYYY-MM-DD HH:MM:SS") or ISO.
export const parseServerDate = (value) => {
  if (!value) return null;
  if (typeof value === "string" && !value.includes("T")) {
    const [d, t = "0:0:0"] = value.split(" ");
    const [y, m, day] = d.split("-").map(Number);
    const [hh, mm, ss] = t.split(":").map(Number);
    const date = new Date(Date.UTC(y, m - 1, day, hh || 0, mm || 0, ss || 0));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateTimeFormat = new Intl.DateTimeFormat("el-GR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export const formatServerDate = (value) => {
  const date = parseServerDate(value);
  return date ? dateTimeFormat.format(date) : "";
};

// eak looks like "<ΕΑΚ>-dd/mm/yyyy"; the date part is the filing date.
const eakDate = (eak) => {
  const part = typeof eak === "string" ? eak.split("-")[1] : null;
  return part && /\d{2}\/\d{2}\/\d{4}/.test(part) ? part : null;
};
const eakYmd = (eak) => {
  const d = eakDate(eak);
  if (!d) return null;
  const [day, month, year] = d.split("/").map(Number);
  return year * 10000 + month * 100 + day;
};

const todayYmd = () => {
  const now = new Date();
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
};

const DIADIKASIA_LABELS = {
  taktiki: "Νέα Τακτική",
  mikro: "Νέες Μικροδιαφορές",
  diatagi: "Διαταγή Πληρωμής/Απόδοσης",
  loipa: "Άλλο",
};

const EPAN_BADGES = {
  pending: { label: "Χρειάζεται αίτηση", tone: "danger" },
  filed: { label: "Κατατέθηκε αίτηση", tone: "ok" },
};

export const CaseBadges = ({ singleCase }) => {
  const epan = singleCase?.epanaprosdiorismos;
  const badges = [];
  if (epan?.role === "application") {
    badges.push({ key: "epan", label: "Αίτηση επαναπρ.", tone: "info" });
  } else if (epan?.role === "old" && EPAN_BADGES[epan.status]) {
    badges.push({ key: "epan", ...EPAN_BADGES[epan.status] });
  }
  if (singleCase?.pretrial_order) {
    badges.push({ key: "pretrial", label: "Διάταξη προδικασίας", tone: "warn" });
  }
  if (!badges.length) return null;
  return badges.map((b) => (
    <span key={b.key} className={`ds-badge ds-badge--${b.tone}`}>
      {b.label}
    </span>
  ));
};

const Ellipsis = ({ children, title }) => (
  <span className="ds-ellipsis" title={title ?? (typeof children === "string" ? children : undefined)}>
    {children}
  </span>
);

// Column definitions for the Αστικά table.
// sortValue returns a number/string; empty values always sort last.
// flex columns share the width left over by the fixed ones (`width` is then
// their minimum before the table starts scrolling horizontally).
export const ASTIKA_COLUMNS = [
  {
    id: "gak",
    label: "ΓΑΚ",
    width: 110,
    defaultVisible: true,
    sortValue: (c) => (Number(c.year) || 0) * 1e7 + (Number(c.gak) || 0),
    render: (c) => (
      <span className="ds-strong">
        {c.gak}/{c.year}
      </span>
    ),
  },
  {
    id: "note",
    flex: true,
    label: "Σημείωση",
    width: 220,
    defaultVisible: true,
    sortValue: (c) => c.note?.trim() || "",
    render: (c) => (
      <span className="ds-cell-flex">
        <Ellipsis>{c.note?.trim() || ""}</Ellipsis>
        <CaseBadges singleCase={c} />
      </span>
    ),
  },
  {
    id: "court",
    flex: true,
    label: "Δικαστήριο",
    width: 170,
    defaultVisible: true,
    sortValue: (c) => c.court || "",
    render: (c) => <Ellipsis title={c.court}>{c.court ? truncateCourt(c.court) : ""}</Ellipsis>,
  },
  {
    id: "diadikasia",
    flex: true,
    label: "Διαδικασία",
    width: 160,
    defaultVisible: true,
    sortValue: (c) => c.diadikasia || "",
    render: (c) => <Ellipsis>{c.diadikasia || ""}</Ellipsis>,
  },
  {
    id: "dikasimos",
    label: "Δικάσιμος",
    width: 100,
    defaultVisible: true,
    sortValue: (c) => parseDikasimos(c.arithmos_pinakiou),
    render: (c) => {
      const ymd = parseDikasimos(c.arithmos_pinakiou);
      if (!ymd) return null;
      if (isDikasimosRescheduled(c)) {
        return (
          <s className="ds-muted" title="Η δικάσιμος δεν ισχύει λόγω επαναπροσδιορισμού">
            {formatYmd(ymd)}
          </s>
        );
      }
      const upcoming = ymd >= todayYmd();
      return (
        <span className={upcoming ? "ds-date-upcoming" : "ds-muted"}>
          {formatYmd(ymd)}
        </span>
      );
    },
  },
  {
    id: "apotelesma",
    flex: true,
    label: "Αποτέλεσμα",
    width: 180,
    defaultVisible: true,
    sortValue: (c) => (c.apotelesma?.length > 2 ? c.apotelesma : ""),
    render: (c) => <Ellipsis>{c.apotelesma?.length > 2 ? c.apotelesma : ""}</Ellipsis>,
  },
  {
    id: "last_checked",
    label: "Τελ. έλεγχος",
    width: 120,
    defaultVisible: true,
    sortValue: (c) => parseServerDate(c.last_checked)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.last_checked)}</span>,
  },
  {
    id: "antikeimeno",
    flex: true,
    label: "Αντικείμενο",
    width: 220,
    defaultVisible: false,
    sortValue: (c) => (c.antikeimeno?.length > 2 ? c.antikeimeno : ""),
    render: (c) => <Ellipsis>{c.antikeimeno?.length > 2 ? c.antikeimeno : ""}</Ellipsis>,
  },
  {
    id: "pinakio",
    flex: true,
    label: "Πινάκιο",
    width: 160,
    defaultVisible: false,
    sortValue: (c) => c.arithmos_pinakiou || "",
    render: (c) => <Ellipsis>{c.arithmos_pinakiou || ""}</Ellipsis>,
  },
  {
    id: "apofasi",
    label: "Αρ. Απόφασης",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => (c.arithmos_apofasis?.length > 2 ? c.arithmos_apofasis : ""),
    render: (c) => (
      <Ellipsis>{c.arithmos_apofasis?.length > 2 ? c.arithmos_apofasis : ""}</Ellipsis>
    ),
  },
  {
    id: "katathesi",
    label: "Ημ. κατάθεσης",
    width: 110,
    defaultVisible: false,
    sortValue: (c) => eakYmd(c.eak),
    render: (c) => <span>{eakDate(c.eak) ?? ""}</span>,
  },
  {
    id: "created",
    label: "Δημιουργία",
    width: 120,
    defaultVisible: false,
    sortValue: (c) => parseServerDate(c.created_at)?.getTime() ?? null,
    render: (c) => <span className="ds-muted">{formatServerDate(c.created_at)}</span>,
  },
  {
    id: "appeal",
    flex: true,
    label: "Ένδικο μέσο",
    width: 160,
    defaultVisible: false,
    sortValue: (c) => c.appeal || "",
    render: (c) => <Ellipsis>{c.appeal || ""}</Ellipsis>,
  },
  {
    id: "selected_diadikasia",
    flex: true,
    label: "Επιλ. διαδικασία",
    width: 150,
    defaultVisible: false,
    sortValue: (c) => DIADIKASIA_LABELS[getSelectedDiadikasia(c)] || "",
    render: (c) => <Ellipsis>{DIADIKASIA_LABELS[getSelectedDiadikasia(c)] || ""}</Ellipsis>,
  },
];

export const ASTIKA_COLUMNS_BY_ID = Object.fromEntries(
  ASTIKA_COLUMNS.map((col) => [col.id, col])
);

// Fixed (non-configurable) sort keys for the ★ column.
export const FAV_COLUMN = {
  id: "fav",
  sortValue: (c) => (c.fav === 1 || c.fav === true ? 1 : 0),
};
